import { useEffect, useRef, useState } from 'react';
import { useAuthUser } from '@nxsys/hooks/AuthHooks';
import { useActionGate } from '@nxsys/hooks/useCan';
import { useUrgentActionsSummary } from '@nxsys/hooks/api/urgentActions/useUrgentActionsSummary';
import { useUrgentActions } from '@nxsys/hooks/api/urgentActions/useUrgentActions';
import { useSnoozeUrgentAction } from '@nxsys/hooks/api/urgentActions/useSnoozeUrgentAction';
import UrgentActionsBanner from '@nxsys/components/UrgentActionsBanner';
import UrgentActionsPopup from '@nxsys/components/UrgentActionsPopup';
import { roles } from '@nxsys/store/constants';

// Use the roles constant (values are UPPERCASE, e.g. SUPPER_ADMIN) — never hardcode.
const ALLOWED_ROLES: string[] = [roles.supper_admin, roles.admin, roles.employer];
const LOGIN_POPUP_FLAG = 'SHOW_URGENT_POPUP';

const isSnoozed = (until?: string | null) =>
  !!until && new Date(until) > new Date();

/**
 * Orchestrates the urgent-actions banner + login popup (NX-5160).
 * Owns the summary/actions/snooze hooks so banner + popup never double-fetch.
 */
const UrgentActions = () => {
  const { user } = useAuthUser();
  // The popup sends the user to RTI filings, whose screen allows the urgent
  // actions; a login without it is refused them (NX-6365).
  const allowed = useActionGate(
    'dashboard:viewUrgentActions',
    ALLOWED_ROLES.includes(user?.role ?? ''),
  );

  const summary = useUrgentActionsSummary();
  const actions = useUrgentActions();
  const snooze = useSnoozeUrgentAction();
  const [popupOpen, setPopupOpen] = useState(false);
  const loginChecked = useRef(false);

  // Banner data on mount.
  useEffect(() => {
    if (allowed) summary.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  // Auto-open popup once after login, unless server says it's snoozed.
  useEffect(() => {
    if (!allowed || loginChecked.current) return;
    if (!sessionStorage.getItem(LOGIN_POPUP_FLAG)) return;
    loginChecked.current = true;
    sessionStorage.removeItem(LOGIN_POPUP_FLAG);
    actions
      .run()
      .then((res) => {
        if (!isSnoozed(res?.popupSnoozedUntil) && res?.actions?.length) {
          setPopupOpen(true);
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const openPopup = () => {
    if (!actions.value) actions.run();
    setPopupOpen(true);
  };

  const handleSnooze = async () => {
    try {
      await snooze.run();
    } finally {
      setPopupOpen(false);
    }
  };

  if (!allowed) return null;

  return (
    <>
      <UrgentActionsBanner summary={summary.value} onClick={openPopup} />
      <UrgentActionsPopup
        open={popupOpen}
        actions={actions.value?.actions ?? []}
        loading={actions.loading}
        snoozing={snooze.loading}
        onClose={() => setPopupOpen(false)}
        onSnooze={handleSnooze}
      />
    </>
  );
};

export default UrgentActions;
